"use client";

import { useState, type ReactNode } from "react";

import { Button } from "@/design-system";
import { T, useT } from "@/lib/i18n/T";
import { FacetLabel } from "./FacetLabel";
import type { Facets, FacetValue } from "@/lib/catalog/types";

/**
 * Catalog filters.
 *
 * Every control writes to the query string rather than to local state: a
 * filtered catalog is then a URL someone can bookmark or send, and the back
 * button behaves the way a shopper expects.
 *
 * On desktop this is a sticky sidebar; under 1000px the same markup becomes a
 * drawer, because the alternative — the old behaviour — was hiding the filters
 * entirely on the devices where scrolling 30k products hurts most.
 *
 * Facet labels come from the backend, so they get the two-tier treatment: a
 * category that also exists in the nav (Dresses, Tops, …) is rendered from its
 * message id and is correct instantly, while the ~130 deeper ones the nav does
 * not name fall back to WorldLida. Without this an Italian page would list its
 * filters in English.
 */

const RATINGS = [4, 3, 2];
/** How many facet values to show before "show all". */
const COLLAPSED = 8;

export interface SelectedFilters {
  categories: string[];
  brands: string[];
  priceMin?: number;
  priceMax?: number;
  minRating?: number;
}

type Update = (mutate: (params: URLSearchParams) => void) => void;

/** Toggle one value of a repeatable parameter. */
function toggleParam(params: URLSearchParams, name: string, value: string) {
  const current = params.getAll(name);
  params.delete(name);
  for (const v of current) if (v !== value) params.append(name, v);
  if (!current.includes(value)) params.append(name, value);
}

function FacetGroup({
  titleId,
  values,
  selected,
  name,
  onChange,
  locale,
  section,
  translateLabels = false,
}: {
  titleId: string;
  values: FacetValue[];
  selected: string[];
  name: string;
  onChange: Update;
  locale: string;
  section?: string;
  /** Categories map to nav labels; brand names are proper nouns, left alone. */
  translateLabels?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  if (values.length === 0) return null;

  // Anything already selected stays visible even when the list is collapsed —
  // a filter you cannot see is a filter you cannot remove.
  const visible = expanded
    ? values
    : [
        ...values.slice(0, COLLAPSED),
        ...values.slice(COLLAPSED).filter((v) => selected.includes(v.value)),
      ];

  return (
    <section className="rb-filter-group">
      <h4><T id={titleId} /></h4>
      <ul>
        {visible.map((v) => {
          const active = selected.includes(v.value);
          return (
            <li key={v.value}>
              <label className={`rb-filter-check ${active ? "active" : ""}`}>
                <input
                  type="checkbox"
                  checked={active}
                  onChange={() => onChange((p) => toggleParam(p, name, v.value))}
                />
                <span className="rb-filter-box" aria-hidden="true" />
                <span className="rb-filter-label">
                  {translateLabels ? (
                    <FacetLabel section={section} value={v.value} label={v.label} />
                  ) : (
                    v.label
                  )}
                </span>
                <span className="rb-filter-count">{v.count.toLocaleString(locale)}</span>
              </label>
            </li>
          );
        })}
      </ul>
      {values.length > COLLAPSED && (
        <button type="button" className="rb-filter-more" onClick={() => setExpanded((e) => !e)}>
          {expanded ? <T id="catalog.filters.showLess" /> : <T id="catalog.filters.showAll" />}
        </button>
      )}
    </section>
  );
}

function PriceGroup({
  facets,
  selected,
  onChange,
}: {
  facets: Facets | null;
  selected: SelectedFilters;
  onChange: Update;
}) {
  const t = useT();

  // The inputs are a draft: you can type "1" on the way to "150" without the
  // catalog refetching for every keystroke. It commits on blur or Enter.
  const applied = `${selected.priceMin ?? ""}|${selected.priceMax ?? ""}`;
  const [syncedFrom, setSyncedFrom] = useState(applied);
  const [min, setMin] = useState(selected.priceMin?.toString() ?? "");
  const [max, setMax] = useState(selected.priceMax?.toString() ?? "");

  // Adjusting state during render — React's documented alternative to a sync
  // effect. Keeps the inputs honest when the URL changes from somewhere else
  // (a chip removed, "clear all", the back button) without an extra pass.
  if (syncedFrom !== applied) {
    setSyncedFrom(applied);
    setMin(selected.priceMin?.toString() ?? "");
    setMax(selected.priceMax?.toString() ?? "");
  }

  const bounds = {
    min: facets?.price_min ? Number(facets.price_min.decimal) : undefined,
    max: facets?.price_max ? Number(facets.price_max.decimal) : undefined,
  };

  function apply() {
    onChange((p) => {
      const set = (key: string, raw: string) => {
        const n = Number(raw);
        if (raw.trim() === "" || !Number.isFinite(n) || n < 0) p.delete(key);
        else p.set(key, String(n));
      };
      set("price_min", min);
      set("price_max", max);
    });
  }

  return (
    <section className="rb-filter-group">
      <h4><T id="catalog.filters.price" /></h4>
      <div className="rb-filter-price">
        <input
          type="number"
          inputMode="decimal"
          min={0}
          value={min}
          placeholder={bounds.min != null ? String(Math.floor(bounds.min)) : t("catalog.filters.min")}
          aria-label={t("catalog.filters.min")}
          onChange={(e) => setMin(e.target.value)}
          onBlur={apply}
          onKeyDown={(e) => e.key === "Enter" && apply()}
        />
        <span aria-hidden="true">—</span>
        <input
          type="number"
          inputMode="decimal"
          min={0}
          value={max}
          placeholder={bounds.max != null ? String(Math.ceil(bounds.max)) : t("catalog.filters.max")}
          aria-label={t("catalog.filters.max")}
          onChange={(e) => setMax(e.target.value)}
          onBlur={apply}
          onKeyDown={(e) => e.key === "Enter" && apply()}
        />
      </div>
    </section>
  );
}

export function FilterPanel({
  facets,
  selected,
  locale,
  section,
  open,
  onClose,
  onChange,
}: {
  facets: Facets | null;
  selected: SelectedFilters;
  locale: string;
  /** Browse mode only — lets category labels reuse the nav's translations. */
  section?: string;
  open: boolean;
  onClose: () => void;
  onChange: Update;
}) {
  const t = useT();

  const chips: { key: string; label: ReactNode; clear: (p: URLSearchParams) => void }[] = [];
  for (const value of selected.categories) {
    const label = facets?.categories.find((c) => c.value === value)?.label ?? value;
    chips.push({
      key: `c:${value}`,
      label: <FacetLabel section={section} value={value} label={label} />,
      clear: (p) => toggleParam(p, "category", value),
    });
  }
  for (const value of selected.brands) {
    const label = facets?.brands.find((b) => b.value === value)?.label ?? value;
    chips.push({ key: `b:${value}`, label, clear: (p) => toggleParam(p, "brand", value) });
  }
  if (selected.priceMin != null || selected.priceMax != null) {
    chips.push({
      key: "price",
      label: `${selected.priceMin ?? "…"} — ${selected.priceMax ?? "…"}`,
      clear: (p) => {
        p.delete("price_min");
        p.delete("price_max");
      },
    });
  }
  if (selected.minRating != null) {
    chips.push({
      key: "rating",
      label: `★ ${selected.minRating}+`,
      clear: (p) => p.delete("rating"),
    });
  }

  function clearAll() {
    onChange((p) => {
      for (const key of ["category", "brand", "price_min", "price_max", "rating"]) p.delete(key);
    });
  }

  return (
    <>
      {open && <div className="rb-filter-scrim" onClick={onClose} aria-hidden="true" />}

      <aside className={`rb-filter-panel ${open ? "is-open" : ""}`} aria-label={t("catalog.filters.title")}>
        <header className="rb-filter-head">
          <h3><T id="catalog.filters.title" /></h3>
          <button type="button" className="rb-filter-close" onClick={onClose} aria-label={t("common.close")}>
            ✕
          </button>
        </header>

        {chips.length > 0 && (
          <section className="rb-filter-active">
            <div className="rb-filter-chips">
              {chips.map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  className="rb-filter-chip"
                  onClick={() => onChange(chip.clear)}
                >
                  {chip.label}
                  <span aria-hidden="true">✕</span>
                </button>
              ))}
            </div>
            <button type="button" className="rb-filter-clear" onClick={clearAll}>
              <T id="catalog.filters.clearAll" />
            </button>
          </section>
        )}

        <FacetGroup
          titleId="catalog.filters.categories"
          values={facets?.categories ?? []}
          selected={selected.categories}
          name="category"
          onChange={onChange}
          locale={locale}
          section={section}
          translateLabels
        />

        <FacetGroup
          titleId="catalog.filters.brands"
          values={facets?.brands ?? []}
          selected={selected.brands}
          name="brand"
          onChange={onChange}
          locale={locale}
        />

        <PriceGroup facets={facets} selected={selected} onChange={onChange} />

        <section className="rb-filter-group">
          <h4><T id="catalog.filters.rating" /></h4>
          <ul>
            {RATINGS.map((r) => {
              const active = selected.minRating === r;
              return (
                <li key={r}>
                  <label className={`rb-filter-check ${active ? "active" : ""}`}>
                    <input
                      type="radio"
                      name="rating"
                      checked={active}
                      onChange={() =>
                        onChange((p) => (active ? p.delete("rating") : p.set("rating", String(r))))
                      }
                    />
                    <span className="rb-filter-box rb-filter-box--radio" aria-hidden="true" />
                    <span className="rb-filter-label">
                      <span className="rb-filter-stars" aria-hidden="true">
                        {"★".repeat(r)}
                        <span>{"★".repeat(5 - r)}</span>
                      </span>
                      <T id="catalog.filters.andUp" />
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </section>

        {facets === null && <p className="rb-filter-empty"><T id="catalog.loading" /></p>}

        <div className="rb-filter-apply">
          <Button variant="solid" fullWidth uppercase onClick={onClose}>
            <T id="catalog.filters.apply" />
          </Button>
        </div>
      </aside>
    </>
  );
}
